import React from 'react';
import { MoreHorizontal, Edit, Trash2, Eye, EyeOff, Star, StarOff } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { BankLogo } from '../ui/bank-logo';
import { 
  PaymentAccount, 
  PaymentAccountStatus,
  formatPaymentAccountType,
  formatPaymentAccountStatus,
  getPaymentAccountStatusColor,
  getPaymentAccountTypeColor,
  formatCurrency
} from '../../types/payment-account';

interface PaymentAccountTableProps {
  accounts: PaymentAccount[];
  selectedAccounts: Set<string>;
  onSelectAccount: (accountId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onEdit: (account: PaymentAccount) => void;
  onDelete: (accountId: string) => void;
}

export function PaymentAccountTable({
  accounts,
  selectedAccounts,
  onSelectAccount,
  onSelectAll,
  onEdit,
  onDelete
}: PaymentAccountTableProps) {
  const allSelected = accounts.length > 0 && accounts.every(a => selectedAccounts.has(a.id));
  const someSelected = accounts.some(a => selectedAccounts.has(a.id));
  const headerCheckedState = allSelected ? true : someSelected ? 'indeterminate' : false;

  const handleToggleStatus = (account: PaymentAccount) => {
    // TODO: Implement status toggle
  };

  const handleToggleDefault = (account: PaymentAccount) => {
    // TODO: Implement default toggle
  };

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'text-green-600';
    if (balance < 0) return 'text-red-600';
    return 'text-foreground';
  };

  const formatAccountNumber = (account: PaymentAccount) => {
    if (account.account_number) {
      // Mask account number for security
      const number = account.account_number;
      if (number.includes('*')) return number; // Already masked
      if (number.length > 6) {
        return `****${number.slice(-4)}`;
      }
      return number;
    }
    return '-';
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-12">
              <Checkbox
                checked={headerCheckedState}
                onCheckedChange={(checked) => onSelectAll(checked === true)}
                aria-label="Selecionar todas as contas"
              />
            </TableHead>
            <TableHead>Conta</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Banco</TableHead>
            <TableHead>Número da Conta</TableHead>
            <TableHead>Saldo Atual</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Transações</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                Nenhuma conta encontrada
              </TableCell>
            </TableRow>
          ) : (
            accounts.map((account) => (
              <TableRow 
                key={account.id}
                className="group hover:bg-muted/50 transition-colors"
              >
                <TableCell>
                  <Checkbox
                    checked={selectedAccounts.has(account.id)}
                    onCheckedChange={(checked) => 
                      onSelectAccount(account.id, checked as boolean)
                    }
                    aria-label={`Selecionar conta ${account.name}`}
                  />
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-3">
                    {account.bank_name ? (
                      <BankLogo
                        bankName={account.bank_name}
                        size="small"
                        showFallback={true}
                      />
                    ) : (
                      <Avatar className="h-8 w-8">
                        <AvatarFallback
                          className="text-xs font-medium bg-muted text-foreground"
                        >
                          {account.name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{account.name}</span>
                        {account.is_default && (
                          <Star className="h-3 w-3 text-yellow-500 fill-current" />
                        )}
                      </div>
                      {account.description && (
                        <div className="text-xs text-muted-foreground truncate">
                          {account.description}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                
                <TableCell>
                  <Badge 
                    variant="outline" 
                    className={`${getPaymentAccountTypeColor(account.type)} text-xs`}
                  >
                    {formatPaymentAccountType(account.type)}
                  </Badge>
                </TableCell>
                
                <TableCell>
                  <div className="text-sm">
                    {account.bank_name || '-'}
                  </div>
                  {account.agency && (
                    <div className="text-xs text-muted-foreground">
                      Ag: {account.agency}
                    </div>
                  )}
                </TableCell>
                
                <TableCell>
                  <div className="text-sm font-mono">
                    {formatAccountNumber(account)}
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className={`text-sm font-bold ${getBalanceColor(account.current_balance)}`}>
                    {formatCurrency(account.current_balance)}
                  </div>
                  {account.initial_balance !== account.current_balance && (
                    <div className="text-xs text-muted-foreground">
                      Inicial: {formatCurrency(account.initial_balance)}
                    </div>
                  )}
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="outline" 
                      className={`${getPaymentAccountStatusColor(account.status)} text-xs`}
                    >
                      {formatPaymentAccountStatus(account.status)}
                    </Badge>
                    {account.status === PaymentAccountStatus.ACTIVE && (
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    )}
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="text-sm font-medium">
                    {account.transaction_count || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    movimentos
                  </div>
                </TableCell>
                
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Abrir menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem 
                        onClick={() => onEdit(account)}
                        className="gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Editar
                      </DropdownMenuItem>
                      
                      <DropdownMenuItem 
                        onClick={() => handleToggleDefault(account)}
                        className="gap-2"
                      >
                        {account.is_default ? (
                          <>
                            <StarOff className="h-4 w-4" />
                            Remover padrão
                          </>
                        ) : (
                          <>
                            <Star className="h-4 w-4" />
                            Definir como padrão
                          </>
                        )}
                      </DropdownMenuItem>
                      
                      <DropdownMenuItem 
                        onClick={() => handleToggleStatus(account)}
                        className="gap-2"
                      >
                        {account.status === PaymentAccountStatus.ACTIVE ? (
                          <>
                            <EyeOff className="h-4 w-4" />
                            Desativar
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4" />
                            Ativar
                          </>
                        )}
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      
                      <DropdownMenuItem 
                        onClick={() => onDelete(account.id)}
                        className="gap-2 text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
